import { useCallback, useEffect, useRef, useState } from "react";

const TYPEAHEAD_RESET_MS = 500;

export interface UseListboxNavOptions {
  itemCount: number;
  /** Typeahead text by index. */
  getLabel: (index: number) => string;
  isDisabled?: (index: number) => boolean;
  onSelect: (index: number) => void;
  onDismiss?: () => void;
  /** Wire to the popup's close; omit for an in-page listbox. */
  onTabOut?: () => void;
}

export interface UseListboxNavResult {
  /** -1 until something is active. */
  activeIndex: number;
  setActiveIndex: React.Dispatch<React.SetStateAction<number>>;
  handleKeyDown: (event: React.KeyboardEvent) => void;
}

/** Headless WAI-ARIA listbox/menu keyboard handling; owns only the active index. */
export function useListboxNav(
  options: UseListboxNavOptions,
): UseListboxNavResult {
  const [requestedIndex, setActiveIndex] = useState(-1);

  // Clamped on read, not in an effect, so a shrinking list never paints a stale index.
  const activeIndex = Math.min(requestedIndex, options.itemCount - 1);

  const optionsRef = useRef(options);
  useEffect(() => {
    optionsRef.current = options;
  });

  const queryRef = useRef("");
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const resetTypeahead = useCallback(() => {
    queryRef.current = "";
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => resetTypeahead, [resetTypeahead]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      const { itemCount, getLabel, isDisabled, onSelect, onDismiss, onTabOut } =
        optionsRef.current;

      const enabled = (index: number) => !isDisabled?.(index);

      const seek = (
        from: number,
        direction: 1 | -1,
        allowLoop: boolean,
      ): number => {
        if (itemCount === 0) return -1;
        let index = from;
        for (let step = 0; step < itemCount; step += 1) {
          index += direction;
          if (index < 0 || index > itemCount - 1) {
            if (!allowLoop) return -1;
            index = index < 0 ? itemCount - 1 : 0;
          }
          if (enabled(index)) return index;
        }
        return -1;
      };

      const moveTo = (next: number) => {
        if (next === -1) return;
        event.preventDefault();
        setActiveIndex(next);
      };

      switch (event.key) {
        case "ArrowDown":
          resetTypeahead();
          moveTo(seek(activeIndex, 1, true));
          return;
        case "ArrowUp":
          resetTypeahead();
          moveTo(seek(activeIndex, -1, true));
          return;
        case "Home":
          resetTypeahead();
          moveTo(seek(-1, 1, false));
          return;
        case "End":
          resetTypeahead();
          moveTo(seek(itemCount, -1, false));
          return;
        case "Escape":
          resetTypeahead();
          if (onDismiss) {
            event.preventDefault();
            onDismiss();
          }
          return;
        case "Tab":
          // No preventDefault: the browser tabs on from wherever `onTabOut` left focus (the trigger).
          resetTypeahead();
          onTabOut?.();
          return;
        case "Enter":
          if (
            activeIndex >= 0 &&
            activeIndex < itemCount &&
            enabled(activeIndex)
          ) {
            event.preventDefault();
            resetTypeahead();
            onSelect(activeIndex);
          }
          return;
        default:
          break;
      }

      // Mid-search, Space is part of the typed text.
      if (event.key === " " && queryRef.current === "") {
        if (
          activeIndex >= 0 &&
          activeIndex < itemCount &&
          enabled(activeIndex)
        ) {
          event.preventDefault();
          onSelect(activeIndex);
        }
        return;
      }

      const isPrintable =
        event.key.length === 1 &&
        !event.ctrlKey &&
        !event.metaKey &&
        !event.altKey;
      if (!isPrintable) return;

      event.preventDefault();
      queryRef.current += event.key.toLowerCase();
      if (timerRef.current !== null) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        queryRef.current = "";
        timerRef.current = null;
      }, TYPEAHEAD_RESET_MS);

      // Repeating one character cycles through items starting with it (APG).
      const query = queryRef.current;
      const prefix = /^(.)\1*$/.test(query) ? query.slice(0, 1) : query;

      for (let step = 1; step <= itemCount; step += 1) {
        const index =
          (Math.max(activeIndex, -1) + step + itemCount) % itemCount;
        if (!enabled(index)) continue;
        if (getLabel(index).toLowerCase().startsWith(prefix)) {
          setActiveIndex(index);
          return;
        }
      }
    },
    [activeIndex, resetTypeahead],
  );

  return { activeIndex, setActiveIndex, handleKeyDown };
}
